import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2,
  ArrowRight,
  Eye,
  EyeOff,
  Mail,
  LockKeyhole,
  ShieldCheck,
  AlertCircle,
  FileText,
  CalendarClock,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { cn } from "@/lib/utils";

/* Hero photo (Unsplash, design guidelines). Served responsively via srcSet. */
const HERO_BASE = "https://images.unsplash.com/photo-1621922688758-359fc864071e?crop=entropy&cs=srgb&fm=jpg&q=80";
const HERO_SRCSET = [720, 1080, 1600, 2000].map((w) => `${HERO_BASE}&w=${w} ${w}w`).join(", ");

const FEATURES = [
  { icon: FileText, title: "Rapportar som PDF", text: "Service- og jobbrapportar med bilete, klare til kunden." },
  { icon: CalendarClock, title: "Fristar under kontroll", text: "Service og årskontroll – sjå kva som nærmar seg." },
  { icon: Wrench, title: "Heile maskinparken", text: "Kundar, maskiner, oljar og filter på ein stad." },
];

const stagger = {
  hidden: { opacity: 0, y: 14 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1], delay: 0.08 * i } }),
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate("/");
    } catch (err) {
      setError(err?.response?.data?.detail || "Innlogging feila. Kontroller e-post og passord.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-[100dvh] bg-ink text-foreground lg:grid lg:grid-cols-[minmax(0,1.1fr)_minmax(440px,0.9fr)] xl:grid-cols-[minmax(0,1.2fr)_minmax(480px,0.8fr)]"
      data-testid="login-page"
    >
      {/* ------------------------------------------------------------------ */}
      {/* Visual side                                                          */}
      {/* ------------------------------------------------------------------ */}
      <section
        className="relative h-[46dvh] min-h-[320px] overflow-hidden bg-ink lg:h-auto lg:min-h-[100dvh]"
        data-testid="login-hero"
        aria-label="Vest Maskinservice"
      >
        <img
          src={`${HERO_BASE}&w=1600`}
          srcSet={HERO_SRCSET}
          sizes="(min-width: 1024px) 55vw, 100vw"
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          decoding="async"
          onLoad={() => setImgLoaded(true)}
          className={cn(
            "absolute inset-0 h-full w-full object-cover object-[60%_40%] saturate-[0.92] transition-opacity duration-700 ease-out lg:object-center",
            imgLoaded ? "opacity-100" : "opacity-0"
          )}
        />
        {/* Readability overlays (decorative) */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/15" />
        <div className="pointer-events-none absolute inset-0 hidden bg-gradient-to-r from-ink/55 via-transparent to-transparent lg:block" />
        <div className="pointer-events-none absolute inset-0 hidden bg-gradient-to-l from-ink/70 to-transparent lg:block lg:w-40 lg:left-auto lg:right-0" />

        <div className="relative z-10 flex h-full flex-col justify-between px-5 pb-12 pt-[max(1.25rem,env(safe-area-inset-top))] sm:px-8 sm:pt-7 lg:p-12 lg:pb-14">
          {/* Top row */}
          <motion.div variants={stagger} initial="hidden" animate="show" custom={0} className="flex items-center justify-between gap-4">
            <Logo onDark height={34} className="drop-shadow-[0_2px_10px_rgba(0,0,0,0.45)]" />
            <span className="hidden items-center gap-2 rounded-full border border-white/15 bg-ink/45 px-3 py-1.5 text-xs font-semibold text-white/90 backdrop-blur-md sm:inline-flex">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              Driftsystem for service og vedlikehald
            </span>
          </motion.div>

          {/* Headline */}
          <div className="max-w-2xl">
            <motion.h2
              variants={stagger}
              initial="hidden"
              animate="show"
              custom={1}
              className="font-heading text-[2rem] font-extrabold leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-[3.4rem] xl:text-6xl"
              data-testid="login-hero-title"
            >
              Full kontroll på
              <br className="hidden sm:block" /> <span className="text-primary">maskinparken</span>.
            </motion.h2>
            <motion.p
              variants={stagger}
              initial="hidden"
              animate="show"
              custom={2}
              className="mt-3 max-w-md text-sm text-white/75 sm:mt-5 sm:text-base lg:text-lg"
            >
              Registrer service, lag profesjonelle rapportar og få oversikt før fristane går ut – rett frå mobilen i felt.
            </motion.p>

            {/* Feature list – desktop only */}
            <ul className="mt-10 hidden gap-3 lg:grid lg:grid-cols-3" aria-label="Funksjonar">
              {FEATURES.map(({ icon: Icon, title, text }, i) => (
                <motion.li
                  key={title}
                  variants={stagger}
                  initial="hidden"
                  animate="show"
                  custom={3 + i}
                  className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 backdrop-blur-md"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-ink">
                    <Icon className="h-[18px] w-[18px]" strokeWidth={2.25} />
                  </span>
                  <div className="mt-3 text-sm font-semibold text-white">{title}</div>
                  <div className="mt-1 text-xs leading-relaxed text-white/65">{text}</div>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Form side                                                            */}
      {/* ------------------------------------------------------------------ */}
      <section
        className="brand-haze relative z-20 -mt-7 flex min-h-[54dvh] flex-col rounded-t-[28px] bg-background shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.55)] lg:mt-0 lg:min-h-0 lg:rounded-none lg:shadow-none"
        data-testid="login-panel"
      >
        <div className="flex flex-1 items-start justify-center px-5 pb-6 pt-8 sm:px-10 sm:pt-12 lg:items-center lg:px-14 lg:py-16 xl:px-20">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            className="w-full max-w-[400px]"
          >
            <div className="mb-8 sm:mb-10">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">Velkomen tilbake</p>
              <h1 className="mt-2 font-heading text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">Logg inn</h1>
              <p className="mt-2 text-sm text-muted-foreground">Bruk kontoen din hjå Vest Maskinservice AS.</p>
            </div>

            <form onSubmit={submit} className="space-y-5" data-testid="login-form" noValidate>
              {/* E-post */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-semibold">E-post</Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                  <Input
                    id="email"
                    type="email"
                    data-testid="login-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="namn@vestmaskinservice.no"
                    autoComplete="username"
                    inputMode="email"
                    autoCapitalize="none"
                    spellCheck={false}
                    required
                    className="h-[52px] rounded-xl border-border bg-card pl-12 text-base shadow-sm transition-[border-color,box-shadow] focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-0"
                  />
                </div>
              </div>

              {/* Passord */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-semibold">Passord</Label>
                </div>
                <div className="relative">
                  <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                  <Input
                    id="password"
                    type={showPw ? "text" : "password"}
                    data-testid="login-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Passordet ditt"
                    autoComplete="current-password"
                    required
                    className="h-[52px] rounded-xl border-border bg-card pl-12 pr-14 text-base shadow-sm transition-[border-color,box-shadow] focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-0"
                  />
                  <button
                    type="button"
                    data-testid="login-toggle-password"
                    onClick={() => setShowPw((s) => !s)}
                    aria-label={showPw ? "Skjul passord" : "Vis passord"}
                    aria-pressed={showPw}
                    className="absolute right-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                  >
                    {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <AnimatePresence initial={false}>
                {error && (
                  <motion.div
                    key="err"
                    initial={{ opacity: 0, y: -6, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: "auto" }}
                    exit={{ opacity: 0, y: -6, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div
                      role="alert"
                      data-testid="login-error"
                      className="status-overdue flex items-start gap-2.5 rounded-xl border px-3.5 py-3 text-sm font-medium"
                    >
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{error}</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <Button
                type="submit"
                size="lg"
                data-testid="login-submit-button"
                disabled={busy}
                className="group h-[54px] w-full rounded-xl text-base font-semibold shadow-[0_10px_30px_-10px_rgba(255,78,0,0.65)] transition-[transform,box-shadow,background-color] hover:-translate-y-px hover:shadow-[0_14px_34px_-10px_rgba(255,78,0,0.7)] active:translate-y-0"
              >
                {busy ? (
                  <>
                    <Loader2 className="animate-spin" /> Loggar inn …
                  </>
                ) : (
                  <>
                    Logg inn
                    <ArrowRight className="transition-transform duration-200 group-hover:translate-x-0.5" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-8 space-y-3">
              <div className="flex items-center gap-2.5 text-xs text-muted-foreground" data-testid="login-security-note">
                <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />
                Sikker innlogging · Berre for ansatte i Vest Maskinservice AS
              </div>
              <p className="text-xs text-muted-foreground" data-testid="login-help-text">
                Har du gløymt passordet eller manglar konto? Ta kontakt med administrator.
              </p>
            </div>
          </motion.div>
        </div>

        <footer className="px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-2 text-[11px] text-muted-foreground/80 sm:px-10 lg:px-14 xl:px-20">
          © {new Date().getFullYear()} Vest Maskinservice AS
        </footer>
      </section>
    </div>
  );
}
